import React, { useState, useMemo, useEffect } from "react";
import { Plus, Search, Edit2, Trash2, X, Package, Check, AlertCircle, Database, LayoutGrid, ClipboardCheck, ShoppingBag, Gem, ExternalLink, Truck, Calendar } from "lucide-react";
import { useSupplies } from "../hooks/useSupplies";
import { useSupplyPurchases } from "../hooks/useSupplyPurchases";
import BomSubTab from "./BomSubTab"; // NOVO IMPORT
import SupplyConferenceSubTab from "./SupplyConferenceSubTab"; // NOVO IMPORT
import SupplyPurchasesSubTab from "./SupplyPurchasesSubTab"; // NOVO IMPORT
import ZirconiaSubTab from "./ZirconiaSubTab"; // NOVO IMPORT

// Função auxiliar para formatar o fornecedor (links clicáveis e WhatsApp/ligação)
function formatSupplier(text) {
  if (!text) return null;
  const trimmed = text.trim();
  
  // Regex para checar se é um link completo ou abreviado
  const urlRegex = /^(https?:\/\/|www\.)[^\s/$.?#].[^\s]*/i;
  // Regex para checar se é um telefone
  const digits = trimmed.replace(/\D/g, "");
  const isPhone = digits.length >= 8 && digits.length <= 15 && /^[+\d\s().-]+$/.test(trimmed);

  if (urlRegex.test(trimmed)) {
    const href = trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-0.5 font-medium"
        onClick={(e) => e.stopPropagation()}
      >
        {trimmed}
        <ExternalLink size={10} />
      </a>
    );
  }

  if (isPhone) {
    const telHref = `tel:${digits}`;
    const waHref = `https://wa.me/${digits.startsWith("55") ? digits : `55${digits}`}`;
    
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-slate-700">
        <a
          href={telHref}
          className="text-blue-600 hover:text-blue-800 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {trimmed}
        </a>
        {digits.length >= 10 && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 hover:text-emerald-800 hover:underline font-bold text-[10px]"
            onClick={(e) => e.stopPropagation()}
          >
            (WhatsApp)
          </a>
        )}
      </span>
    );
  }

  // Se tiver um link no meio do texto
  const words = trimmed.split(/\s+/);
  const hasUrl = words.some(w => urlRegex.test(w));
  if (hasUrl) {
    return (
      <span>
        {words.map((word, idx) => {
          if (urlRegex.test(word)) {
            const href = word.startsWith("http") ? word : `https://${word}`;
            return (
              <React.Fragment key={idx}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-0.5 font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  {word}
                  <ExternalLink size={8} />
                </a>{" "}
              </React.Fragment>
            );
          }
          return <span key={idx}>{word} </span>;
        })}
      </span>
    );
  }

  return <span className="font-medium text-slate-700">{trimmed}</span>;
}

export default function SuppliesTab() {
  const {
    supplies,
    isLoading,
    availableSectors,
    availableCategories,
    availableUnits,
    fetchSupplies,
    addSupply,
    editSupply,
    removeSupply,
  } = useSupplies();

  const { purchases } = useSupplyPurchases();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterOverdueOnly, setFilterOverdueOnly] = useState(false);
  const [activeSectorTab, setActiveSectorTab] = useState("todos"); // 'todos' ou nome do setor
  const [activeSubTab, setActiveSubTab] = useState(() => {
    return localStorage.getItem("supplies_active_subtab") || "base";
  });

  useEffect(() => {
    localStorage.setItem("supplies_active_subtab", activeSubTab);
  }, [activeSubTab]);

  const [isCategoryUpdating, setIsCategoryUpdating] = useState(false); // loader especial de categoria

  const supplyReplenishmentMap = useMemo(() => {
    const map = {};
    if (!purchases) return map;
    purchases.forEach((p) => {
      if (p.status === "draft" || p.status === "ordered") {
        const items = Array.isArray(p.items) ? p.items : [];
        items.forEach((item) => {
          if (item.supply_id) {
            if (!map[item.supply_id]) {
              map[item.supply_id] = { draft: 0, ordered: 0, unit: item.unit };
            }
            const upc = Number(item.unidades_por_compra) || 1;
            const qty = Number(item.ordered_quantity) || 0;
            if (p.status === "draft") {
              map[item.supply_id].draft += qty * upc;
            } else {
              map[item.supply_id].ordered += qty * upc;
            }
          }
        });
      }
    });
    return map;
  }, [purchases]);

  const overdueCount = useMemo(() => {
    return supplies.filter((s) => {
      if (s.sector === "pedras") return false;
      let meta = s.metadata;
      if (typeof meta === "string") {
        try { meta = JSON.parse(meta); } catch (e) {}
      }
      if (meta?.monitorado_por_tempo) {
        const daysFreq = Number(meta?.periodo_verificacao_dias) || 7;
        const lastCheckStr = meta?.ultima_verificacao || s.created || new Date().toISOString();
        const lastCheckDate = new Date(lastCheckStr);
        const nextCheckDate = new Date(lastCheckDate.getTime() + daysFreq * 24 * 60 * 60 * 1000);
        
        const todayMidnight = new Date();
        todayMidnight.setHours(0, 0, 0, 0);
        const nextCheckMidnight = new Date(nextCheckDate);
        nextCheckMidnight.setHours(0, 0, 0, 0);
        
        return todayMidnight > nextCheckMidnight;
      }
      return false;
    }).length;
  }, [supplies]);
  
  const filteredSectors = useMemo(() => {
    return availableSectors.filter(s => s !== "pedras");
  }, [availableSectors]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSupply, setEditingSupply] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    sector: "",
    category: "",
    unit: "",
    min_quantity: "",
    current_quantity: "",
    custo_unidade_compra: "",
    unidades_por_compra: "1",
    custo_unitario: "",
    fornecedor: "",
    monitorado_por_tempo: false,
    periodo_verificacao_dias: "7",
    ultima_verificacao: new Date().toISOString().split("T")[0],
  });

  const [newSectorMode, setNewSectorMode] = useState(false);
  const [newCategoryMode, setNewCategoryMode] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredSupplies = useMemo(() => {
    return supplies.filter((s) => {
      // Exclui pedras da listagem de insumos comuns
      if (s.sector === "pedras") return false;

      const matchSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchSector = activeSectorTab === "todos" ? true : s.sector === activeSectorTab;
      
      let matchOverdue = true;
      if (filterOverdueOnly) {
        let meta = s.metadata;
        if (typeof meta === "string") {
          try { meta = JSON.parse(meta); } catch (e) {}
        }
        if (meta?.monitorado_por_tempo) {
          const daysFreq = Number(meta?.periodo_verificacao_dias) || 7;
          const lastCheckStr = meta?.ultima_verificacao || s.created || new Date().toISOString();
          const lastCheckDate = new Date(lastCheckStr);
          const nextCheckDate = new Date(lastCheckDate.getTime() + daysFreq * 24 * 60 * 60 * 1000);
          
          const todayMidnight = new Date();
          todayMidnight.setHours(0, 0, 0, 0);
          const nextCheckMidnight = new Date(nextCheckDate);
          nextCheckMidnight.setHours(0, 0, 0, 0);
          
          matchOverdue = todayMidnight > nextCheckMidnight;
        } else {
          matchOverdue = false; // se não for monitorado, não está atrasado
        }
      }
      
      return matchSearch && matchSector && matchOverdue;
    });
  }, [supplies, searchTerm, activeSectorTab, filterOverdueOnly]);

  const groupedByCategory = useMemo(() => {
    const groups = {};
    filteredSupplies.forEach((s) => {
      const cat = s.category && s.category.trim() !== "" ? s.category.trim() : "Sem Categoria";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(s);
    });
    return groups;
  }, [filteredSupplies]);

  const sortedCategories = useMemo(() => {
    const keys = Object.keys(groupedByCategory);
    return keys.sort((a, b) => {
      if (a === "Sem Categoria") return 1;
      if (b === "Sem Categoria") return -1;
      return a.localeCompare(b);
    });
  }, [groupedByCategory]);

  const handleRemoveCategory = async (categoryName) => {
    const itemsInCat = supplies.filter(s => s.category === categoryName);
    if (itemsInCat.length === 0) return;
    
    if (!window.confirm(`Deseja remover a categoria "${categoryName}" de todos os ${itemsInCat.length} insumos? Eles ficarão como "Sem Categoria".`)) {
      return;
    }
    
    setIsCategoryUpdating(true);
    try {
      const updatePromises = itemsInCat.map(item => 
        editSupply(item.id, {
          name: item.name,
          sector: item.sector,
          category: "", // zera a categoria
          unit: item.unit,
          min_quantity: item.min_quantity,
          current_quantity: item.current_quantity,
          custo_unidade_compra: item.custo_unidade_compra !== undefined ? item.custo_unidade_compra : 0,
          unidades_por_compra: item.unidades_por_compra !== undefined ? item.unidades_por_compra : 1,
          custo_unitario: item.custo_unitario !== undefined ? item.custo_unitario : 0,
        })
      );
      await Promise.all(updatePromises);
      alert(`Categoria "${categoryName}" removida com sucesso!`);
    } catch (e) {
      alert("Erro ao remover categoria: " + e.message);
    } finally {
      setIsCategoryUpdating(false);
    }
  };

  const openModal = (supply = null) => {
    if (supply) {
      setEditingSupply(supply);
      
      let currentFornecedor = "";
      let monitoradoPorTempo = false;
      let periodoVerificacao = "7";
      let ultimaVerificacao = new Date().toISOString().split("T")[0];

      if (supply.metadata) {
        let meta = supply.metadata;
        if (typeof meta === "string") {
          try { meta = JSON.parse(meta); } catch (e) {}
        }
        if (meta && typeof meta === "object") {
          currentFornecedor = meta.fornecedor || "";
          monitoradoPorTempo = !!meta.monitorado_por_tempo;
          periodoVerificacao = meta.periodo_verificacao_dias !== undefined ? String(meta.periodo_verificacao_dias) : "7";
          if (meta.ultima_verificacao) {
            try {
              ultimaVerificacao = new Date(meta.ultima_verificacao).toISOString().split("T")[0];
            } catch (e) {}
          }
        }
      }

      setFormData({
        name: supply.name || "",
        sector: supply.sector || "",
        category: supply.category || "",
        unit: supply.unit || "",
        min_quantity: supply.min_quantity || "",
        current_quantity: supply.current_quantity || "",
        custo_unidade_compra: supply.custo_unidade_compra !== undefined ? supply.custo_unidade_compra : "",
        unidades_por_compra: supply.unidades_por_compra !== undefined ? supply.unidades_por_compra : "1",
        custo_unitario: supply.custo_unitario !== undefined ? supply.custo_unitario : "",
        fornecedor: currentFornecedor,
        monitorado_por_tempo: monitoradoPorTempo,
        periodo_verificacao_dias: periodoVerificacao,
        ultima_verificacao: ultimaVerificacao,
      });
      setNewSectorMode(supply.sector && !availableSectors.includes(supply.sector));
      setNewCategoryMode(supply.category && !availableCategories.includes(supply.category));
    } else {
      setEditingSupply(null);
      setFormData({
        name: "",
        sector: "",
        category: "",
        unit: "",
        min_quantity: "",
        current_quantity: "",
        custo_unidade_compra: "",
        unidades_por_compra: "1",
        custo_unitario: "",
        fornecedor: "",
        monitorado_por_tempo: false,
        periodo_verificacao_dias: "7",
        ultima_verificacao: new Date().toISOString().split("T")[0],
      });
      setNewSectorMode(false);
      setNewCategoryMode(false);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSupply(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let existingMetadata = {};
      if (editingSupply?.metadata) {
        if (typeof editingSupply.metadata === "string") {
          try {
            existingMetadata = JSON.parse(editingSupply.metadata);
          } catch (e) {}
        } else if (typeof editingSupply.metadata === "object") {
          existingMetadata = editingSupply.metadata;
        }
      }

      const isPedras = formData.sector.trim().toLowerCase() === "pedras";

      const payload = {
        name: formData.name.trim(),
        sector: formData.sector.trim().toLowerCase(),
        category: formData.category.trim().toLowerCase(),
        unit: formData.unit.trim().toLowerCase(),
        min_quantity: Number(formData.min_quantity) || 0,
        current_quantity: Number(formData.current_quantity) || 0,
        custo_unidade_compra: formData.custo_unidade_compra !== "" ? Number(formData.custo_unidade_compra) : 0,
        unidades_por_compra: formData.unidades_por_compra !== "" ? Number(formData.unidades_por_compra) : 1,
        custo_unitario: formData.custo_unitario !== "" ? Number(formData.custo_unitario) : 0,
        metadata: {
          ...existingMetadata,
          fornecedor: !isPedras ? (formData.fornecedor || "").trim() : "",
          monitorado_por_tempo: !!formData.monitorado_por_tempo,
          periodo_verificacao_dias: Number(formData.periodo_verificacao_dias) || 7,
          ultima_verificacao: formData.monitorado_por_tempo 
            ? new Date(formData.ultima_verificacao + "T12:00:00").toISOString() 
            : null,
        },
      };

      if (editingSupply) {
        await editSupply(editingSupply.id, payload);
      } else {
        await addSupply(payload);
      }
      closeModal();
    } catch (err) {
      alert("Erro ao salvar insumo: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Tem certeza que deseja remover "${name}"?`)) return;
    try {
      await removeSupply(id);
    } catch (err) {
      alert("Erro ao excluir. Pode estar vinculado a alguma BOM.");
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden w-full">
      
      {/* NAVEGAÇÃO DE SUB-ABAS (Fase 2, Fase 3, Fase 4...) */}
      <div className="flex px-4 pt-4 border-b border-slate-200 bg-white gap-2 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden">
         <button 
            onClick={() => setActiveSubTab("base")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeSubTab === "base" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
         >
            <LayoutGrid size={16}/> Gestão Física (Itens)
         </button>
         <button 
            onClick={() => setActiveSubTab("bom")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeSubTab === "bom" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
         >
            <Database size={16}/> Matriz BOM (Receitas)
         </button>
         <button 
            onClick={() => setActiveSubTab("conference")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeSubTab === "conference" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
         >
            <ClipboardCheck size={16}/> Modo Conferência
         </button>
         <button 
            onClick={() => setActiveSubTab("purchases")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeSubTab === "purchases" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
         >
            <ShoppingBag size={16}/> Compras & Recebimento
         </button>
         <button 
            onClick={() => setActiveSubTab("zirconias")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeSubTab === "zirconias" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
         >
            <Gem size={16}/> Estoque de Pedras (Zircônias)
         </button>
      </div>

      {/* --- RENDERIZA O CONTEÚDO CORRETO --- */}
      
      <div className={`flex flex-col h-full overflow-hidden ${activeSubTab === "base" ? "block" : "hidden"}`}>
        <div className="flex flex-col h-full gap-4 p-4 lg:p-6 overflow-hidden">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shrink-0">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="text-blue-600" /> Gestão de Insumos
          </h2>
          <p className="text-sm text-slate-500">
            Cadastre os itens e categorias disponíveis para produção e conferência.
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow transition-colors w-full sm:w-auto justify-center"
        >
          <Plus size={20} /> Novo Insumo
        </button>
      </div>

      {/* Filters & Sub-tabs */}
      <div className="flex flex-col gap-4 shrink-0">
        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Buscar insumo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
            />
          </div>
          {overdueCount > 0 && (
            <button
              onClick={() => setFilterOverdueOnly(!filterOverdueOnly)}
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-bold text-xs shadow-sm transition-all border shrink-0 ${
                filterOverdueOnly
                  ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                  : "bg-white text-red-600 border-red-200 hover:bg-red-50"
              }`}
            >
              <AlertCircle size={14} className={filterOverdueOnly ? "" : "animate-pulse"} />
              Apenas Atrasados ({overdueCount})
            </button>
          )}
        </div>

        {/* SETORES COMO SUB-ABAS HORIZONTAIS */}
        <div className="flex gap-2 border-b border-slate-200 pb-2 overflow-x-auto no-scrollbar shrink-0">
          <button
            onClick={() => setActiveSectorTab("todos")}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 ${
              activeSectorTab === "todos"
                ? "bg-slate-800 text-white shadow-sm"
                : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            <span>TODOS</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${
              activeSectorTab === "todos" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"
            }`}>
              {supplies.filter(s => s.sector !== "pedras").length}
            </span>
          </button>
          
          {filteredSectors.map((sec) => {
            const count = supplies.filter(s => s.sector === sec).length;
            return (
              <button
                key={sec}
                onClick={() => setActiveSectorTab(sec)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 uppercase ${
                  activeSectorTab === sec
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                <span>{sec}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                  activeSectorTab === sec ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid / Table Grouped by Category */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {isLoading || isCategoryUpdating ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-500 bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
            <span>{isCategoryUpdating ? "Processando alteração de categoria..." : "Carregando insumos..."}</span>
          </div>
        ) : (
          <div className="space-y-6 overflow-y-auto pr-1 pb-20 custom-scrollbar flex-1">
            {sortedCategories.map((category) => {
              const catItems = groupedByCategory[category];
              
              return (
                <div key={category} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden transition-all hover:shadow-md">
                  {/* Category Header */}
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                      <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
                        Categoria: <span className="text-blue-700 normal-case ml-1 font-bold">{category}</span>
                      </h3>
                      <span className="bg-slate-200 text-slate-700 text-[10px] font-extrabold px-2 py-0.5 rounded-full ml-2">
                        {catItems.length} {catItems.length === 1 ? "item" : "itens"}
                      </span>
                    </div>

                    {category !== "Sem Categoria" && (
                      <button
                        onClick={() => handleRemoveCategory(category)}
                        className="text-slate-400 hover:text-red-600 p-1.5 rounded hover:bg-red-50 transition-all flex items-center gap-1 text-xs font-bold border border-transparent hover:border-red-100"
                        title="Remover Categoria de todos os itens"
                      >
                        <Trash2 size={13} />
                        <span className="hidden sm:inline">Excluir Categoria</span>
                      </button>
                    )}
                  </div>

                  {/* Table of Items */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-slate-50/50 border-b border-slate-100">
                        <tr>
                          <th className="p-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider pl-4">
                            Insumo
                          </th>
                          <th className="p-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                            Setor
                          </th>
                          <th className="p-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-right pr-6">
                            Custo Unitário
                          </th>
                          <th className="p-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-right pr-6">
                            Saldo Atual / Mínimo
                          </th>
                          <th className="p-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-center w-24">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {catItems.map((s) => {
                          const isUnavailable = Number(s.current_quantity) <= 0;
                          const isLowStock = !isUnavailable && Number(s.current_quantity) <= Number(s.min_quantity);
                          
                          let meta = s.metadata;
                          if (typeof meta === "string") {
                            try { meta = JSON.parse(meta); } catch (e) {}
                          }

                          const forn = meta?.fornecedor;

                          // Lógica de Monitoramento por tempo
                          const isTimeMonitored = !!meta?.monitorado_por_tempo;
                          const daysFreq = Number(meta?.periodo_verificacao_dias) || 7;
                          const lastCheckStr = meta?.ultima_verificacao || s.created || new Date().toISOString();
                          const lastCheckDate = new Date(lastCheckStr);
                          const nextCheckDate = new Date(lastCheckDate.getTime() + daysFreq * 24 * 60 * 60 * 1000);
                          
                          const todayMidnight = new Date();
                          todayMidnight.setHours(0, 0, 0, 0);
                          const nextCheckMidnight = new Date(nextCheckDate);
                          nextCheckMidnight.setHours(0, 0, 0, 0);
                          
                          const isOverdue = isTimeMonitored && todayMidnight > nextCheckMidnight;

                          // Reposições pendentes (Rascunho e Em Trânsito)
                          const repData = supplyReplenishmentMap[s.id];

                          return (
                            <tr key={s.id} className={`hover:bg-blue-50/20 transition-colors ${isOverdue ? "bg-red-50/10" : ""}`}>
                              <td className="p-3 pl-4">
                                <div className="font-semibold text-slate-800">{s.name}</div>
                                {forn && forn.trim() !== "" && (
                                  <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                                    <span className="font-medium text-slate-400">Fornecedor:</span>
                                    {formatSupplier(forn)}
                                  </div>
                                )}
                                {isTimeMonitored && (
                                  <div className="text-[11px] mt-1.5 flex items-center gap-2 flex-wrap bg-slate-50 border border-slate-200 rounded-lg p-1.5 max-w-fit shadow-xs">
                                    <span className="font-bold text-slate-500 flex items-center gap-1">
                                      <Calendar size={11} className="text-indigo-500" /> Verificação Periódica ({daysFreq}d):
                                    </span>
                                    {isOverdue ? (
                                      <span className="text-[10px] text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.2 rounded font-extrabold uppercase animate-pulse">
                                        Atrasada!
                                      </span>
                                    ) : (
                                      <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.2 rounded font-bold">
                                        Em dia (Próx: {nextCheckDate.toLocaleDateString("pt-BR")})
                                      </span>
                                    )}
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const updatedMeta = {
                                            ...meta,
                                            ultima_verificacao: new Date().toISOString()
                                          };
                                          await editSupply(s.id, {
                                            name: s.name,
                                            sector: s.sector,
                                            category: s.category,
                                            unit: s.unit,
                                            min_quantity: s.min_quantity,
                                            current_quantity: s.current_quantity,
                                            custo_unidade_compra: s.custo_unidade_compra || 0,
                                            unidades_por_compra: s.unidades_por_compra || 1,
                                            custo_unitario: s.custo_unitario || 0,
                                            metadata: updatedMeta
                                          });
                                          alert(`Insumo "${s.name}" marcado como verificado hoje!`);
                                        } catch (err) {
                                          alert("Erro ao salvar verificação: " + err.message);
                                        }
                                      }}
                                      className="text-[9px] bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-2 py-0.5 rounded transition-colors shadow-xs ml-1 flex items-center gap-0.5"
                                      title="Confirmar estoque físico hoje"
                                    >
                                      <Check size={8} /> Confirmar Hoje
                                    </button>
                                  </div>
                                )}
                              </td>
                              <td className="p-3">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-[10px] font-bold text-slate-600 uppercase">
                                  {s.sector || "-"}
                                </span>
                              </td>
                              <td className="p-3 text-right pr-6 whitespace-nowrap">
                                <div className="font-bold text-slate-700 font-mono">
                                  R$ {Number(s.custo_unitario || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                                </div>
                                {Number(s.unidades_por_compra) > 1 && (
                                  <div className="text-[9px] text-slate-400 mt-0.5">
                                    Compra: R$ {Number(s.custo_unidade_compra || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} (c/ {s.unidades_por_compra})
                                  </div>
                                )}
                              </td>
                              <td className="p-3 text-right pr-6">
                                <div className="flex flex-col items-end gap-1.5">
                                  <div className="flex items-center gap-2">
                                    {isUnavailable && (
                                      <span className="text-[9px] text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full font-black uppercase flex items-center gap-0.5 animate-pulse">
                                        <AlertCircle size={9} /> Sem Estoque
                                      </span>
                                    )}
                                    {isLowStock && (
                                      <span className="text-[9px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full font-black uppercase flex items-center gap-0.5">
                                        <AlertCircle size={9} /> Baixo
                                      </span>
                                    )}
                                    <div className={`font-bold text-base ${isUnavailable ? "text-red-600" : isLowStock ? "text-amber-600" : "text-emerald-600"}`}>
                                      {s.current_quantity} <span className="text-xs font-normal text-slate-400">{s.unit}</span>
                                    </div>
                                    <span className="text-xs text-slate-400">/ Mín: {s.min_quantity}</span>
                                  </div>
                                  
                                  {/* Badge de Reposição Pendente */}
                                  {repData && (
                                    <div className="flex flex-col items-end gap-1">
                                      {repData.draft > 0 && (
                                        <span className="text-[9px] text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-md font-semibold flex items-center gap-0.5" title="Quantidade em rascunho de pedido">
                                          <ShoppingBag size={8} /> Rascunho: +{repData.draft} {s.unit}
                                        </span>
                                      )}
                                      {repData.ordered > 0 && (
                                        <span className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-md font-semibold flex items-center gap-0.5" title="Quantidade comprada em trânsito">
                                          <Truck size={8} className="text-amber-500" /> Em Trânsito: +{repData.ordered} {s.unit}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex items-center justify-center gap-1.5">
                                  <button
                                    onClick={() => openModal(s)}
                                    className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    title="Editar"
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(s.id, s.name)}
                                    className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Excluir"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
            
            {filteredSupplies.length === 0 && (
              <div className="text-center py-10 text-slate-400 animate-fade-in bg-white rounded-xl border border-slate-200">
                <Package size={48} className="mx-auto mb-2 opacity-20" />
                <p>Nenhum insumo encontrado nesta pesquisa.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-full animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center p-6 border-b border-slate-100 shrink-0">
              <h3 className="text-lg font-bold text-slate-800">
                {editingSupply ? "Editar Insumo" : "Cadastrar Insumo"}
              </h3>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <form id="supply-form" onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nome do Insumo *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Ex: Ouro 18k, Caixa Premium, Papel Manteiga"
                  />
                </div>

                {formData.sector?.trim().toLowerCase() !== "pedras" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Fornecedor
                    </label>
                    <input
                      type="text"
                      value={formData.fornecedor}
                      onChange={(e) =>
                        setFormData({ ...formData, fornecedor: e.target.value })
                      }
                      className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Link, telefone ou informações de onde compra"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Dropdown/Input de Setor */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Setor *
                    </label>
                    {!newSectorMode ? (
                      <select
                        required
                        value={availableSectors.includes(formData.sector) || formData.sector === "" ? formData.sector : "NEW"}
                        onChange={(e) => {
                          if (e.target.value === "NEW") {
                            setNewSectorMode(true);
                            setFormData({ ...formData, sector: "" });
                          } else {
                            setFormData({ ...formData, sector: e.target.value });
                          }
                        }}
                        className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        <option value="" disabled>Selecione um setor...</option>
                        {availableSectors.map((s) => (
                          <option key={s} value={s}>{s.toUpperCase()}</option>
                        ))}
                        <option value="NEW" className="font-bold text-blue-600">+ Cadastrar Novo Setor...</option>
                      </select>
                    ) : (
                      <div className="flex gap-2 relative">
                        <input
                          type="text"
                          required
                          autoFocus
                          value={formData.sector}
                          onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                          placeholder="Digite o novo setor..."
                          className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => {
                             setNewSectorMode(false);
                             if (availableSectors.length > 0) {
                               setFormData({ ...formData, sector: "" });
                             }
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-red-500"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Dropdown/Input de Categoria */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Categoria *
                    </label>
                    {!newCategoryMode ? (
                      <select
                        required
                        value={availableCategories.includes(formData.category) || formData.category === "" ? formData.category : "NEW"}
                        onChange={(e) => {
                          if (e.target.value === "NEW") {
                            setNewCategoryMode(true);
                            setFormData({ ...formData, category: "" });
                          } else {
                            setFormData({ ...formData, category: e.target.value });
                          }
                        }}
                        className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        <option value="" disabled>Selecione a categoria...</option>
                        {availableCategories.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                        <option value="NEW" className="font-bold text-blue-600">+ Cadastrar Nova Categoria...</option>
                      </select>
                    ) : (
                      <div className="flex gap-2 relative">
                        <input
                          type="text"
                          required
                          autoFocus
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          placeholder="Digite a nova categoria..."
                          className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => {
                             setNewCategoryMode(false);
                             if (availableCategories.length > 0) {
                               setFormData({ ...formData, category: "" });
                             }
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-red-500"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Unidade */}
                  <div className="sm:col-span-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Unidade de Medida
                    </label>
                    <input
                      type="text"
                      required
                      list="units-list"
                      value={formData.unit}
                      onChange={(e) =>
                        setFormData({ ...formData, unit: e.target.value })
                      }
                      className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="un, g, kg, l..."
                    />
                    <datalist id="units-list">
                      {availableUnits.map((u) => (
                        <option key={u} value={u} />
                      ))}
                    </datalist>
                  </div>

                  <div className="sm:col-span-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1 text-red-600 font-bold">
                      Qtd. Alerta Mínima
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      required
                      value={formData.min_quantity}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          min_quantity: e.target.value,
                        })
                      }
                      className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    />
                  </div>

                  <div className="sm:col-span-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Saldo Atual (Físico)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      required
                      value={formData.current_quantity}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          current_quantity: e.target.value,
                        })
                      }
                      className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-slate-850 font-medium"
                    />
                  </div>
                </div>

                {/* SEÇÃO DE MONITORAMENTO POR TEMPO */}
                <div className="border-t border-slate-100 pt-4 mt-2">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                    Monitoramento de Inventário
                  </h4>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={formData.monitorado_por_tempo}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            monitorado_por_tempo: e.target.checked,
                          })
                        }
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                      />
                      <span className="text-sm font-medium text-slate-700">
                        Monitorar por Tempo (Verificação Periódica)
                      </span>
                    </label>

                    {formData.monitorado_por_tempo && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-3 rounded-lg border border-slate-200 animate-in fade-in slide-in-from-top-1 duration-150">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1">
                            Frequência (Dias) *
                          </label>
                          <input
                            type="number"
                            min="1"
                            required
                            value={formData.periodo_verificacao_dias}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                periodo_verificacao_dias: e.target.value,
                              })
                            }
                            className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 font-medium"
                            placeholder="Ex: 7"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1">
                            Última Verificação *
                          </label>
                          <input
                            type="date"
                            required
                            value={formData.ultima_verificacao}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                ultima_verificacao: e.target.value,
                              })
                            }
                            className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 font-medium"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* SEÇÃO DE CUSTOS & EMBALAGEM */}
                <div className="border-t border-slate-100 pt-4 mt-2">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                    Custos & Embalagem de Compra
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Custo da Compra (R$)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={formData.custo_unidade_compra}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData((prev) => {
                            const newCost = Number(val) || 0;
                            const packQty = Number(prev.unidades_por_compra) || 1;
                            return {
                              ...prev,
                              custo_unidade_compra: val,
                              custo_unitario: (newCost / packQty).toFixed(4),
                            };
                          });
                        }}
                        className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 font-medium"
                        placeholder="Ex: 100.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Unidades por Compra
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="any"
                        value={formData.unidades_por_compra}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData((prev) => {
                            const newCost = Number(prev.custo_unidade_compra) || 0;
                            const packQty = Number(val) || 1;
                            return {
                              ...prev,
                              unidades_por_compra: val,
                              custo_unitario: (newCost / packQty).toFixed(4),
                            };
                          });
                        }}
                        className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 font-medium"
                        placeholder="Ex: 50"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-1">
                        Custo Unitário (Uso)
                      </label>
                      <div className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-700 font-bold font-mono text-center h-[42px] flex items-center justify-center">
                        R$ {Number(formData.custo_unitario || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-100 shrink-0 bg-slate-50 rounded-b-xl">
              <button
                type="button"
                onClick={closeModal}
                disabled={isSubmitting}
                className="px-5 py-2.5 font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="supply-form"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-2.5 font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg transition-colors shadow-sm disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                ) : (
                  <Check size={18} />
                )}
                {editingSupply ? "Atualizar Insumo" : "Gravar Insumo"}
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>

      {activeSubTab === "bom" && (
        <BomSubTab />
      )}

      {activeSubTab === "conference" && (
        <SupplyConferenceSubTab 
          supplies={supplies} 
          availableSectors={availableSectors} 
          onRefreshSupplies={fetchSupplies} 
        />
      )}

      {activeSubTab === "purchases" && (
        <SupplyPurchasesSubTab 
          supplies={supplies} 
          onRefreshSupplies={fetchSupplies} 
        />
      )}

      {activeSubTab === "zirconias" && (
        <ZirconiaSubTab 
          supplies={supplies}
          onRefreshSupplies={fetchSupplies}
          editSupply={editSupply}
          addSupply={addSupply}
          removeSupply={removeSupply}
        />
      )}

    </div>
  );
}
